/**
 * §8.5 routing dry-run tester — paste an order id, run it through the CURRENT
 * saved ruleset on the worker, and see the full match trace (which rules were
 * tried, which matched, and the winning carrier/service or UNROUTABLE). Mutates
 * nothing.
 */
import { useState } from "react";
import { ApiError, api, type AdminRoutingRule, type RoutingTestResult } from "../../../lib/api.js";
import { humanizeServiceCode } from "../../../lib/format.js";
import { Button, Icon, CarrierMark } from "../../../components/ui/index.js";

export function RoutingTester({ rules, onClose }: { rules: AdminRoutingRule[]; onClose: () => void }) {
  const [orderId, setOrderId] = useState("");
  const [result, setResult] = useState<RoutingTestResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  async function run() {
    const id = orderId.trim();
    if (!id) return;
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      // Test against the live saved rules (source of truth); `rules` is passed
      // so the trace reflects exactly what's listed, including disabled rows.
      const res = await api.admin.testRouting(id, rules);
      setResult(res);
    } catch (e) {
      setError(e instanceof ApiError && e.status === 404 ? "No order with that id." : "Couldn’t run the test.");
    } finally {
      setRunning(false);
    }
  }

  const winnerId = result?.result.ruleId;

  return (
    <div className="tester">
      <div className="tester__bar">
        <Icon name="bolt" size={15} />
        <span className="tester__title">Dry-run tester</span>
        <input
          className="control tester__input"
          placeholder="Order id (cuid)"
          aria-label="Order id"
          value={orderId}
          onChange={(e) => setOrderId(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void run();
          }}
        />
        <Button variant="primary" size="sm" icon="bolt" onClick={() => void run()} disabled={running || !orderId.trim()}>
          {running ? "Running…" : "Run"}
        </Button>
        <button type="button" className="tester__close" aria-label="Close tester" onClick={onClose}>
          <Icon name="x" size={14} />
        </button>
      </div>

      {error && <p className="tester__error">{error}</p>}

      {result && (
        <div className="tester__result">
          <div className="tester__summary">
            <span className="muted">
              {result.order.name} · {result.order.storeRegion} → {result.order.destinationCountry}
            </span>
            {result.result.matched ? (
              <span className="tester__outcome tester__outcome--ok">
                <Icon name="check" size={14} /> {result.result.ruleName} → <CarrierMark code={result.result.carrierCode} showName />
                <span className="cell-sub">{humanizeServiceCode(result.result.serviceCode ?? "")}</span>
              </span>
            ) : (
              <span className="tester__outcome tester__outcome--fail">
                <Icon name="alert" size={14} /> {result.result.exceptionReason ?? "UNROUTABLE"}
              </span>
            )}
          </div>
          <ol className="tester__trace">
            {result.result.trace.map((t) => (
              <li key={t.ruleId} className={"trace-row" + (t.ruleId === winnerId ? " trace-row--win" : "")}>
                <span className="tnum">{t.priority}</span>
                <span className="trace-row__name">{t.ruleName}</span>
                <span className={t.matched ? "trace-row__yes" : "trace-row__no"}>
                  {t.matched ? <Icon name="check" size={13} /> : "—"}
                </span>
              </li>
            ))}
            {result.result.trace.length === 0 && <li className="muted">No rules evaluated.</li>}
          </ol>
        </div>
      )}
    </div>
  );
}
